import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaFlask,
  FaRedo,
  FaShieldAlt,
} from "react-icons/fa";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import {
  formatNumber,
  formatPercent,
  formatSeconds,
  MEASUREMENT_AA_QUERY,
  readinessLabel,
  selectPrimaryMetric,
} from "./engagementAnalyticsUtils";
import "./EngagementAnalytics.css";

function formatDateTime(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Jerusalem",
  }).format(new Date(value));
}

function MetricTile({ label, value, helper, tone = "default" }) {
  return (
    <div className={`engagement-metric-tile engagement-tone-${tone}`}>
      <span className="engagement-metric-label">{label}</span>
      <strong className="engagement-metric-value">{value}</strong>
      {helper && <span className="engagement-metric-helper">{helper}</span>}
    </div>
  );
}

function ArmCard({ label, estimate, metricName }) {
  const isRate = metricName?.endsWith("_rate");
  const displayEstimate = isRate
    ? formatPercent(estimate?.estimate)
    : formatNumber(estimate?.estimate);
  const barWidth = isRate
    ? Math.max(0, Math.min(100, (estimate?.estimate || 0) * 100))
    : 0;

  return (
    <div className={`engagement-arm-card engagement-arm-${label.toLowerCase()}`}>
      <div className="engagement-arm-heading">
        <span>{label}</span>
        <strong>{displayEstimate}</strong>
      </div>
      {isRate && (
        <div className="engagement-arm-track" aria-hidden="true">
          <span style={{ width: `${barWidth}%` }} />
        </div>
      )}
      <div className="engagement-arm-meta">
        <span>{formatNumber(estimate?.n_observations)} observations</span>
        <span>{formatNumber(estimate?.n_clusters)} clusters</span>
        <span>{formatNumber(estimate?.effective_clusters)} effective</span>
      </div>
    </div>
  );
}

const EngagementAnalytics = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await allAPiServicesCall.getEngagementReport(
        MEASUREMENT_AA_QUERY,
        getAuthConfig()
      );
      if (!response) {
        setError("The aggregate report did not return data. Retry once or check API health.");
        return;
      }
      setReport(response);
    } catch (requestError) {
      console.error("Failed to load engagement analytics:", requestError);
      setError("The aggregate report could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const analysis = report?.experiment_analysis;
  const readiness = analysis?.decision_readiness;
  const exposure = analysis?.exposure_audit;
  const overall = report?.overall;
  const coverage = report?.coverage;
  const { metricName, metric: primaryMetric } = useMemo(
    () => selectPrimaryMetric(report),
    [report]
  );
  const srm = exposure?.sample_ratio_mismatch;
  const measuredCoverage = coverage?.song_page_views
    ? coverage.measurement_v2_page_views / coverage.song_page_views
    : null;
  const finalizedCoverage = coverage?.song_page_views
    ? coverage.finalized_page_views / coverage.song_page_views
    : null;
  const reasons = readiness?.reasons || [];

  return (
    <CommonLayout>
      <div className="engagement-analytics-page">
        <section className="engagement-hero">
          <div className="engagement-hero-copy">
            <div className="engagement-icon"><FaFlask /></div>
            <div>
              <div className="engagement-eyebrow">READ-ONLY · AGGREGATE DATA</div>
              <h1>Song Engagement A/A</h1>
              <p>
                Measurement validation for <code>measurement_aa_v1</code>. No UX
                treatment is enabled by this dashboard.
              </p>
            </div>
          </div>
          <button className="engagement-refresh" onClick={loadReport} disabled={loading}>
            <FaRedo className={loading ? "engagement-spin" : ""} />
            {loading ? "Loading…" : "Refresh report"}
          </button>
        </section>

        <section className="engagement-window-strip">
          <div><FaClock /><span>Registered window</span></div>
          <strong>{formatDateTime(MEASUREMENT_AA_QUERY.from)} → {formatDateTime(MEASUREMENT_AA_QUERY.to)}</strong>
          <span>Final decision after ingestion lag: 18 Nov 2026, 02:00 Israel</span>
        </section>

        {error && (
          <div className="engagement-alert engagement-alert-error" role="alert">
            <FaExclamationTriangle /> <span>{error}</span>
          </div>
        )}

        {loading && !report ? (
          <div className="engagement-loading" aria-live="polite">
            <div className="engagement-loader" />
            <strong>Building the aggregate experiment report…</strong>
            <span>This can take up to three minutes on the registered horizon.</span>
          </div>
        ) : report ? (
          <>
            <section className={`engagement-status-card ${readiness?.ready ? "is-ready" : "is-waiting"}`}>
              <div className="engagement-status-icon">
                {readiness?.ready ? <FaCheckCircle /> : <FaClock />}
              </div>
              <div>
                <span className="engagement-status-kicker">Decision readiness</span>
                <h2>{readiness?.ready ? "Ready for the registered decision" : "Collecting evidence"}</h2>
                <p>
                  {readiness?.ready
                    ? "All registered statistical and data-quality gates passed."
                    : `${reasons.length} registered gate${reasons.length === 1 ? "" : "s"} still open.`}
                </p>
              </div>
              <div className="engagement-generated">
                Generated {formatDateTime(report?.meta?.generated_at)}
              </div>
            </section>

            <section className="engagement-grid engagement-grid-coverage">
              <MetricTile label="Exposed page views" value={formatNumber(exposure?.exposed_page_views)} helper={`${formatNumber(coverage?.sessions)} sessions`} />
              <MetricTile label="Song clusters" value={`${formatNumber(exposure?.control_song_clusters)} / ${formatNumber(exposure?.treatment_song_clusters)}`} helper="control / treatment" />
              <MetricTile label="Measurement v2 coverage" value={formatPercent(measuredCoverage)} helper={`${formatNumber(coverage?.measurement_v2_page_views)} measured views`} tone={measuredCoverage >= 0.9 ? "good" : "warning"} />
              <MetricTile label="Finalized coverage" value={formatPercent(finalizedCoverage)} helper={`${formatNumber(coverage?.finalized_page_views)} finalized views`} />
              <MetricTile label="Exposure span" value={`${formatNumber(exposure?.exposure_span_days)} days`} helper="minimum: 7 days" />
              <MetricTile label="Observed treatment share" value={formatPercent(srm?.observed_treatment_share)} helper={`expected ${formatPercent(srm?.expected_treatment_share)}`} tone={srm?.mismatch === false ? "good" : srm?.mismatch ? "danger" : "default"} />
            </section>

            <section className="engagement-panel">
              <div className="engagement-panel-heading">
                <div>
                  <span className="engagement-panel-kicker">PRIMARY METRIC</span>
                  <h2>{metricName?.replaceAll("_", " ") || "Unavailable"}</h2>
                </div>
                <div className="engagement-method-badge">
                  Cluster-randomized by song
                </div>
              </div>

              {primaryMetric ? (
                <>
                  <div className="engagement-arms">
                    <ArmCard label="Control" estimate={primaryMetric.control} metricName={metricName} />
                    <ArmCard label="Treatment" estimate={primaryMetric.treatment} metricName={metricName} />
                  </div>
                  <div className="engagement-inference-grid">
                    <MetricTile
                      label="Absolute difference"
                      value={
                        typeof primaryMetric.absolute_difference === "number"
                          ? metricName?.endsWith("_rate")
                            ? `${(primaryMetric.absolute_difference * 100).toFixed(2)} pp`
                            : formatNumber(primaryMetric.absolute_difference)
                          : "—"
                      }
                    />
                    <MetricTile label="Relative lift" value={formatPercent(primaryMetric.relative_lift)} />
                    <MetricTile
                      label="p-value"
                      value={
                        typeof primaryMetric.p_value === "number"
                          ? primaryMetric.p_value.toFixed(4)
                          : "—"
                      }
                      helper="Not a decision before horizon"
                    />
                    <MetricTile label="80% detectable difference" value={formatPercent(primaryMetric.detectable_absolute_difference_80pct)} />
                  </div>
                </>
              ) : (
                <div className="engagement-empty">Primary inference is not available yet.</div>
              )}
            </section>

            <div className="engagement-two-column">
              <section className="engagement-panel">
                <div className="engagement-panel-heading compact">
                  <div>
                    <span className="engagement-panel-kicker">OPEN GATES</span>
                    <h2>Why the report is not ready</h2>
                  </div>
                </div>
                {reasons.length ? (
                  <ul className="engagement-reason-list">
                    {reasons.map((reason) => (
                      <li key={reason}>
                        <FaExclamationTriangle />
                        <div><strong>{readinessLabel(reason)}</strong><code>{reason}</code></div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="engagement-all-clear"><FaCheckCircle /> All registered gates passed.</div>
                )}
              </section>

              <section className="engagement-panel">
                <div className="engagement-panel-heading compact">
                  <div>
                    <span className="engagement-panel-kicker">SITE BASELINE</span>
                    <h2>Current engagement</h2>
                  </div>
                </div>
                <div className="engagement-baseline-list">
                  <div><span>Active time / page</span><strong>{formatSeconds(overall?.active_time_per_page_seconds?.mean)}</strong></div>
                  <div><span>Median active time</span><strong>{formatSeconds(overall?.active_time_per_page_seconds?.median)}</strong></div>
                  <div><span>Second-song rate</span><strong>{formatPercent(overall?.second_song_rate)}</strong></div>
                  <div><span>Quick-exit rate</span><strong>{formatPercent(overall?.quick_exit_rate)}</strong></div>
                  <div><span>Lyrics seen</span><strong>{formatPercent(overall?.lyrics_seen_rate)}</strong></div>
                  <div><span>Continuation</span><strong>{formatPercent(overall?.continuation_rate)}</strong></div>
                  <div><span>Ad fill</span><strong>{formatPercent(overall?.ad_fill_rate)}</strong></div>
                  <div><span>Ad viewability</span><strong>{formatPercent(overall?.ad_viewability_rate)}</strong></div>
                </div>
              </section>
            </div>

            <section className="engagement-guardrail">
              <FaShieldAlt />
              <div>
                <strong>Interpretation guardrail</strong>
                <span>
                  This A/A validates measurement equivalence. It is not expected to
                  increase dwell time, and early p-values must not be used to stop the
                  registered experiment.
                </span>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </CommonLayout>
  );
};

export default EngagementAnalytics;
